import { useEffect, useState } from 'react';
import Button from '../../../components/base/Button';

export default function HeroSection() {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    // Guard for environments where `window` is not available (e.g., SSR)
    if (typeof window === 'undefined') return;

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    // Cleanup the listener on unmount
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 滚动到联系区域
  const scrollToContact = () => {
    const contactSection = document.getElementById('contact');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // 滚动到专业服务区域
  const scrollToServices = () => {
    const servicesSection = document.getElementById('services');
    if (servicesSection) {
      servicesSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // 滚动互动效果计算
  const scrollProgress = Math.min(scrollY / 800, 1);
  const heroOpacity = Math.max(0.2, 1 - scrollProgress * 0.8);
  const heroScale = Math.max(0.9, 1 - scrollProgress * 0.1);
  const contentTranslateY = scrollProgress * 50;
  const overlayOpacity = Math.min(0.7, 0.4 + scrollProgress * 0.3);

  return (
    <section 
      className="relative min-h-screen flex items-center justify-start overflow-hidden z-10"
      style={{
        opacity: heroOpacity,
        transform: `scale(${heroScale})`,
        transition: 'opacity 0.1s ease-out, transform 0.1s ease-out'
      }}
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 w-full h-full bg-cover bg-center z-0"
        style={{
          backgroundImage: `url('https://static.readdy.ai/image/ccbd9e92c2e4555bab62a0921318c3e7/fb3f4e3a56a9030e9c9fa2ff44cc6fd2.jpeg')`
        }}
      ></div>

      {/* Dynamic overlay with brand colors */}
      <div
        className="absolute inset-0 z-10"
        style={{
          background: `linear-gradient(135deg, rgba(96, 122, 122, 0.8) 0%, rgba(80, 104, 106, 0.9) 100%)`,
          opacity: overlayOpacity,
          transition: 'opacity 0.1s ease-out',
        }}
      ></div>

      <div className="relative z-20 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className="max-w-2xl"
          style={{
            transform: `translateY(${contentTranslateY}px)`,
            transition: 'transform 0.1s ease-out',
          }}
        >
          <div className="space-y-8">
            <div className="space-y-6">
              <h1
                className="text-4xl lg:text-5xl xl:text-6xl font-light text-white leading-tight"
                style={{
                  transform: `translateY(${contentTranslateY * 0.5}px)`,
                  transition: 'transform 0.1s ease-out',
                }}
              >
                Driving Southern Africa{' '}
                <span className="font-normal">Sustainable Growth</span>{' '}
                with Inclusive & Equitable Solutions
              </h1>

              <p
                className="text-xl lg:text-2xl text-white/90 leading-relaxed font-light max-w-xl"
                style={{
                  transform: `translateY(${contentTranslateY * 0.3}px)`,
                  transition: 'transform 0.1s ease-out',
                }}
              >
                Where Equality Drives Opportunity
              </p>
            </div>

            <div
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              style={{
                transform: `translateY(${contentTranslateY * 0.2}px)`,
                opacity: Math.max(0.3, 1 - scrollProgress * 0.7),
                transition:
                  'transform 0.1s ease-out, opacity 0.1s ease-out',
              }}
            >
              <button 
                onClick={scrollToContact}
                className="bg-transparent text-white px-8 py-4 rounded-lg font-medium border-2 border-white hover:bg-white hover:text-slate-900 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Contact Us
              </button>
              <button 
                onClick={scrollToServices}
                className="bg-transparent text-white px-8 py-4 rounded-lg font-medium border-2 border-white hover:bg-white hover:text-slate-900 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Scroll indicator with better visibility */}
      <div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
        style={{
          opacity: Math.max(0, 1 - scrollProgress * 2),
          transition: 'opacity 0.2s ease-out',
        }}
      >
        <div className="flex flex-col items-center space-y-4 bg-white/20 backdrop-blur-md rounded-2xl px-8 py-6 border-2 border-white/40 shadow-2xl">
          <span className="text-white font-semibold text-base tracking-wider uppercase drop-shadow-lg">
            Scroll to explore
          </span>
          <div className="relative">
            {/* Enhanced Mouse outline */}
            <div 
              className="w-10 h-16 border-3 rounded-full flex justify-center relative shadow-lg"
              style={{ 
                borderColor: '#98b8c1',
                borderWidth: '3px',
                boxShadow: '0 0 20px rgba(152, 184, 193, 0.4)'
              }}
            >
              {/* Mouse wheel/scroll indicator */}
              <div 
                className="w-2 h-6 rounded-full mt-3 animate-bounce shadow-sm"
                style={{ 
                  backgroundColor: '#98b8c1',
                  boxShadow: '0 0 10px rgba(152, 184, 193, 0.6)'
                }}
              ></div>
              
              {/* Mouse body highlight */}
              <div 
                className="absolute inset-0 rounded-full bg-white/10 backdrop-blur-sm"
              ></div>
            </div>
            
            {/* Enhanced Scroll arrow indicator */}
            <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2">
              <i 
                className="ri-arrow-down-line text-2xl animate-pulse drop-shadow-lg"
                style={{ color: '#98b8c1' }}
              ></i>
            </div>
          </div>
          
          {/* Additional visual enhancement */}
          <div className="flex space-x-1">
            <div className="w-2 h-2 rounded-full bg-white/60 animate-pulse"></div>
            <div className="w-2 h-2 rounded-full bg-white/40 animate-pulse" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 rounded-full bg-white/20 animate-pulse" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </div>
    </section>
  );
}
